import env from './env';

// Detection for the st-kuori Capacitor shell — mirrors st-toosa's
// domain/native-shell.ts. The shell loads these same hosted pages in a WebView
// and appends `SomisKuori/1.0 (<env>)` to the User-Agent via Capacitor's
// `appendUserAgent`. Matching the bare marker (not version/env) keeps detection
// working across future shell releases.
//
// Pure and globals-free so it unit-tests without stubbing navigator.
export const KUORI_UA_MARKER = 'SomisKuori';

export const isKuoriShell = (userAgent: string): boolean =>
  userAgent.includes(KUORI_UA_MARKER);

// Client-side convenience: reads the live User-Agent. SSR-safe — returns false
// when navigator is unavailable (server render).
export const isInKuoriShell = (): boolean =>
  typeof navigator !== 'undefined' && isKuoriShell(navigator.userAgent);

// The kuori shell writes this cookie natively before its first page load —
// peikko's login origin has no Capacitor bridge, so a cookie is the only way
// the shell can surface device biometric availability to these pages. '1'
// means strong biometry is enrolled and usable; anything else (including a
// missing cookie, i.e. every plain browser) means unavailable.
export const KUORI_BIOMETRIC_COOKIE = 'kuori_biometric';

export const isBiometricAvailable = (
  cookies: Record<string, string | undefined>,
): boolean => cookies[KUORI_BIOMETRIC_COOKIE] === '1';

// Whether to actually offer the "keep me signed in" opt-in.
//
// Device availability alone is not enough. The kuori shell writes
// kuori_biometric on the prod apex, and the released store binaries already
// do so — so gating on that cookie alone renders the checkbox in production
// while REFRESH_COOKIE_KEY is still the `disabled` placeholder. Ticking it
// would then do nothing: scopeForOptIn drops offline_access, no refresh
// cookie is set, and the user is signed out on the normal schedule with no
// error anywhere. A checkbox that silently fails to keep you signed in is
// worse than no checkbox.
//
// KEEP_SIGNED_IN_ENABLED is a boolean published via publicRuntimeConfig (see
// next.config.js), so this is identical on the server and after client-side
// navigation. The key itself is never exposed.
export const isKeepSignedInOffered = (
  cookies: Record<string, string | undefined>,
): boolean => isBiometricAvailable(cookies) && Boolean(env.KEEP_SIGNED_IN_ENABLED);
